/**
 * Q4 — "Does the employee perform in line with all three Leadership Values consistently?"
 * The form has ONE answer (Yes / No / The Employee does not have direct reports); the tool asks
 * per value. Mapping (our call, Oct 30 2026 — to be confirmed by Zohal with the first test
 * version): all three Yes → "Yes", any No → "No". Confirmed by Zohal Nov 3 2026.
 */
import type { Deliverable, Employee, LeadershipData } from '../../types/managerReviewV2';

export const LEADERSHIP_VALUE_NAMES = [
  'Deliver Exceptional Results',
  'Fuel Talent',
  'Push FINN to the next Level',
] as const;

export const NO_DIRECT_REPORTS_ANSWER = 'The Employee does not have direct reports';

export const emptyLeadership = (): LeadershipData => ({ answers: {}, wellDone: '', improvement: '', noDirectReports: null });

/** Shown above the Q4 values (Zohal, Nov 3 2026). */
export const LEADERSHIP_CONTEXT =
  'Leadership is already assessed as part of Delivery. This question gives our Leadership Values additional visibility by explicitly assessing how consistently they are demonstrated in practice, in line with the employee\u2019s Talent Framework.';

/** Whether Q4 applies: the manager's answer wins over the People-database check. */
export function leadershipApplies(employee: Employee | null, data: LeadershipData): boolean {
  if (data.noDirectReports !== null) return !data.noDirectReports;
  return employee?.hasDirectReports === true;
}

/** The manager has answered "Does X have direct reports?" (asked so Q4 can't be skipped by accident). */
export const directReportsAnswered = (data: LeadershipData): boolean => data.noDirectReports !== null;

/** Q4 is part of the review: the manager answered "Yes" (or the answer is still open and the DB says yes → not yet). */
export const leadershipConfirmed = (employee: Employee | null, data: LeadershipData): boolean =>
  directReportsAnswered(data) && leadershipApplies(employee, data);

export function leadershipComplete(employee: Employee | null, data: LeadershipData): boolean {
  if (!directReportsAnswered(data)) return false;
  if (!leadershipApplies(employee, data)) return true;
  return LEADERSHIP_VALUE_NAMES.every(v => data.answers[v] !== undefined);
}

/** Answers one line of a value and re-derives the value's answer from all of its lines. */
export function answerPoint(data: LeadershipData, value: string, index: number, lineCount: number, answer: boolean): LeadershipData {
  const key = `${value}#${index}`;
  const pointAnswers = { ...data.pointAnswers, [key]: data.pointAnswers?.[key] === answer ? undefined : answer };
  const lines = Array.from({ length: lineCount }, (_, i) => pointAnswers[`${value}#${i}`]);
  const derived = lines.every(a => a !== undefined) ? lines.every(a => a === true) : undefined;
  return { ...data, pointAnswers, answers: { ...data.answers, [value]: derived } };
}

/**
 * A restored draft: value answers count only if they come from point answers. Pilot drafts from
 * before the per-point format had value answers only; those values are asked again, unless the
 * review is already submitted (its autosave must not wipe what was recorded).
 */
export function restoreLeadership(saved: Partial<LeadershipData>, submitted = false): LeadershipData {
  const data = { ...emptyLeadership(), ...saved };
  return data.pointAnswers || submitted ? data : { ...data, answers: {} };
}

/** Deliverable chips spell it "Next Level"; the Q4 value is "next Level". */
const isLeadershipTag = (tag: string) => (LEADERSHIP_VALUE_NAMES as readonly string[]).includes(tag.replace('Next Level', 'next Level'));

export interface LeadershipMention { id: string; title: string; values: string[]; wellDone: string; improvement: string }

/**
 * Deliverables the manager tagged with a Leadership Value and wrote notes for: offered to copy into
 * Q4 ("you mentioned it in Delivery", Zohal Nov 3 2026). Those already copied are left out.
 */
export function leadershipMentions(deliverables: Deliverable[], data: LeadershipData): LeadershipMention[] {
  return deliverables.flatMap(d => {
    const values = (d.competencies || []).filter(isLeadershipTag).map(v => v.replace('Next Level', 'next Level'));
    if (!d.id || !values.length || data.copiedFrom?.includes(d.id)) return [];
    const wellDone = d.wellDone.trim(); const improvement = d.improvement.trim();
    return wellDone || improvement ? [{ id: d.id, title: d.title.trim() || 'Deliverable', values, wellDone, improvement }] : [];
  });
}

const append = (prev: string, text: string) => (text ? (prev.trim() ? `${prev.trim()}\n\n${text}` : text) : prev);

/** Copies a mention's notes into the Q4 notes, prefixed with where they came from, and marks it done. */
export function copyMention(data: LeadershipData, m: LeadershipMention): LeadershipData {
  const from = `From Delivery (${m.title}): `;
  return {
    ...data,
    wellDone: append(data.wellDone, m.wellDone ? from + m.wellDone : ''),
    improvement: append(data.improvement, m.improvement ? from + m.improvement : ''),
    copiedFrom: [...(data.copiedFrom || []), m.id],
  };
}

/** Q4 applies but a value or both notes are still empty: warn before leaving the Delivery tab. */
export function leadershipMissingInput(employee: Employee | null, data: LeadershipData): boolean {
  if (!directReportsAnswered(data) || !leadershipApplies(employee, data)) return false;
  const unanswered = LEADERSHIP_VALUE_NAMES.some(v => data.answers[v] === undefined);
  return unanswered || (!data.wellDone.trim() && !data.improvement.trim());
}

/** The Culture Amp answer for Q4, or null while a value is unanswered. */
export function cultureAmpLeadershipAnswer(
  employee: Employee | null,
  data: LeadershipData,
): { answer: string; notMet: string[] } | null {
  if (!directReportsAnswered(data)) return null;
  if (!leadershipApplies(employee, data)) return { answer: NO_DIRECT_REPORTS_ANSWER, notMet: [] };
  if (!leadershipComplete(employee, data)) return null;
  const notMet = LEADERSHIP_VALUE_NAMES.filter(v => data.answers[v] === false);
  return { answer: notMet.length ? 'No' : 'Yes', notMet };
}
